import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Upload, RotateCcw, Save, Plus, Trash2, AlertTriangle, ExternalLink, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FloorPlanSvg, FloorLegend, TABLE_STYLES } from './FloorPlanSvg';
import { FloorHistoryPanel } from './FloorHistoryPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayKst = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60 * 1000);
  return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
};

export const FloorPlanTab = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [plan, setPlan] = useState(null);
  const [date, setDate] = useState(todayKst());
  const [live, setLive] = useState(null);
  const [selected, setSelected] = useState(null);
  const editorData = useMemo(() => {
    if (!plan) return null;
    if (!live) return plan;
    const liveByNum = Object.fromEntries(live.tables.map(t => [t.num, t]));
    const tables = plan.tables.map(t => ({ ...(liveByNum[t.num] || {}), ...t }));
    return { ...live, tables, zones: plan.zones, labels: plan.labels, table_style: plan.table_style, bg_url: plan.bg_url, image_url: plan.image_url };
  }, [live, plan]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [historyKey, setHistoryKey] = useState(0);
  const [previewVersion, setPreviewVersion] = useState(null);

  const loadPlan = useCallback(() => axios.get(`${API}/floor/plan`).then(r => setPlan(r.data)), []);
  useEffect(() => {
    axios.get(`${API}/tournaments/dates`).then(r => {
      const dates = r.data.map(d => d.date).sort();
      const today = todayKst();
      if (dates.includes(today)) return;
      const next = dates.find(d => d > today) || dates[dates.length - 1];
      if (next) setDate(next);
    }).catch(() => {});
  }, []);
  const loadLive = useCallback(() => axios.get(`${API}/floor/live`, { params: { date } }).then(r => setLive(r.data)).catch(() => {}), [date]);
  useEffect(() => { loadPlan(); }, [loadPlan]);
  useEffect(() => { setLive(null); loadLive(); }, [loadLive]);

  const savePlan = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/floor/plan`, plan, { headers });
      setPlan(data); setMsg('Layout saved'); setPreviewVersion(null); setHistoryKey(k => k + 1); loadLive();
    } catch { setMsg('Save failed'); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 2500); }
  };

  const resetPlan = async () => {
    if (!window.confirm('Reset the layout to the default drawing?')) return;
    const { data } = await axios.post(`${API}/admin/floor/plan/reset`, {}, { headers });
    setPlan(data); setPreviewVersion(null); setHistoryKey(k => k + 1); loadLive();
  };

  const previewVersionPlan = (versionPlan, v) => {
    setPlan(p => ({ ...p, ...versionPlan, bg_url: versionPlan.bg_url || p.bg_url, image_url: versionPlan.image_url || p.image_url }));
    setPreviewVersion(v);
    setMsg(`Previewing "${v.name}" — press Save layout to apply`);
    setTimeout(() => setMsg(''), 4000);
  };
  const restoredVersion = (data) => { setPlan(data); setPreviewVersion(null); loadLive(); };

  const uploadImage = (field = 'image_url') => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setUploading(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        const url = `/api/files/${data.path}`;
        if (field === 'bg_url') await applyBackground(url);
        else setPlan(p => ({ ...p, image_url: url }));
      } catch (err) {
        setMsg(`Upload failed: ${err.response?.data?.detail || err.message}`); setTimeout(() => setMsg(''), 5000);
      } finally { setUploading(false); }
    };
    input.click();
  };

  // Background changes are saved straight away (no extra "Save layout" needed) so the board updates within 15s.
  const applyBackground = async (url) => {
    const body = { ...plan, bg_url: url };
    delete body.id;
    const { data } = await axios.put(`${API}/admin/floor/plan`, body, { headers });
    setPlan(data); setHistoryKey(k => k + 1); loadLive();
    setMsg(url ? 'Background uploaded and applied to the board' : 'Background removed'); setTimeout(() => setMsg(''), 4000);
  };

  const pickColor = async (ev, color) => {
    const body = color ? { date, pokerlens_id: ev.pokerlens_id, color } : { date, pokerlens_id: ev.pokerlens_id, reset: true };
    const { data } = await axios.put(`${API}/admin/floor/color`, body, { headers });
    setLive(data);
  };
  const pickShape = async (ev, shape) => {
    const { data } = await axios.put(`${API}/admin/floor/color`, { date, pokerlens_id: ev.pokerlens_id, shape }, { headers });
    setLive(data);
  };

  const assign = async (tableNum, eventId) => {
    const { data } = await axios.put(`${API}/admin/floor/assign`, { date, table: tableNum, event_id: eventId || null }, { headers });
    setLive(data);
  };

  const updateTable = (i, field, value) => {
    setPlan(p => {
      const tables = p.tables.map((t, idx) => idx === i ? { ...t, [field]: field === 'zone' ? value : Number(value) } : t);
      return { ...p, tables };
    });
  };
  const addTable = () => setPlan(p => ({
    ...p,
    tables: [...p.tables, { num: Math.max(0, ...p.tables.map(t => t.num)) + 1, x: 500, y: 950, w: 55, h: 90, zone: 'MAIN AREA', rot: 0 }],
  }));
  const removeTable = (i) => setPlan(p => ({ ...p, tables: p.tables.filter((_, idx) => idx !== i) }));

  if (!plan) return <p className="text-white/40 text-sm">Loading…</p>;

  const selTable = live?.tables.find(t => t.num === selected);

  return (
    <div className="space-y-6" data-testid="floor-plan-tab">
      {/* ---- Today's assignment ---- */}
      <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main">Table assignment</h3>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44 bg-background-base border-white/10 text-white" data-testid="floor-date-input" />
          <Button variant="ghost" size="sm" onClick={loadLive} className="border border-white/10 text-white/70" data-testid="floor-refresh-btn">Refresh</Button>
          <a href={`/billboard/floor?date=${date}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-heading tracking-wider uppercase text-secondary-main hover:underline" data-testid="floor-open-billboard-link">
            <ExternalLink className="w-3.5 h-3.5" /> Open billboard (1080×1920)
          </a>
          <a href="/billboard/floor" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-heading tracking-wider uppercase text-white/60 hover:underline" data-testid="floor-open-live-link">
            <ExternalLink className="w-3.5 h-3.5" /> Live (today, auto-advance)
          </a>
          <span className="text-[11px] text-white/35">Open tables come straight from PokerLens (table number = your table number). Click a table to pin it to an event; yellow border = pinned.</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          <div className="bg-black/40 rounded-lg p-2" style={{ maxHeight: 720, overflow: 'auto' }}>
            <FloorPlanSvg data={editorData} showSeats={false} onTableClick={t => setSelected(t.num)} selectedNum={selected} style={{ width: '100%', height: 'auto' }} />
          </div>
          <div className="space-y-4">
            {live && live.conflicts && live.conflicts.length > 0 && (
              <div className="border border-red-500/50 bg-red-500/10 rounded-lg p-3" data-testid="floor-conflicts">
                <p className="font-heading text-xs uppercase tracking-wider text-red-400 mb-1.5 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Duplicate table numbers in PokerLens</p>
                <ul className="space-y-1">
                  {live.conflicts.map(c => (
                    <li key={c.table} className="text-xs text-white/80" data-testid={`floor-conflict-table-${c.table}`}>
                      <span className="font-bold text-white">Table {c.table}</span> is open in {c.events.map(e => `#${e.event_number} (${e.time})`).join(' and ')} — change the table number in PokerLens.
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {live && live.events.length > 0
              ? <>
                  <p className="text-[11px] text-white/40">Pick the arrow color (first row) and arrow shape (second row) for each tournament — remembered for that tournament.</p>
                  <FloorLegend events={live.events} compact palette={live.palette} onColorPick={pickColor} onShapePick={pickShape} />
                </>
              : <p className="text-white/40 text-sm">No events on this date.</p>}
            {selTable && (
              <div className="border border-secondary-main/40 rounded-lg p-3 space-y-2" data-testid="floor-assign-panel">
                <p className="font-heading text-sm text-white">Table {selTable.num} <span className="text-white/40">· {selTable.zone}</span></p>
                <select
                  data-testid="floor-assign-select"
                  value={selTable.manual ? (selTable.event_id || '') : ''}
                  onChange={e => assign(selTable.num, e.target.value)}
                  className="w-full bg-background-base border border-white/10 rounded-md text-white text-sm p-2"
                >
                  <option value="">Automatic{selTable.event_id && !selTable.manual ? ` (now: #${live.events.find(ev => ev.id === selTable.event_id)?.event_number})` : ''}</option>
                  {live.events.map(ev => <option key={ev.id} value={ev.id}>{ev.time} · #{ev.event_number} {ev.event_name}</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---- Layout editor ---- */}
      <div className="bg-background-surface border border-white/[0.06] rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main flex-1">Floor layout</h3>
          <Button variant="ghost" size="sm" onClick={() => uploadImage('bg_url')} disabled={uploading} className="border border-secondary-main/40 text-secondary-main hover:bg-secondary-main/10" data-testid="floor-upload-bg-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Uploading…' : 'Upload background'}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => uploadImage('image_url')} disabled={uploading} className="border border-white/10 text-white/70" data-testid="floor-upload-image-btn">
            <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Uploading…' : (plan.image_url ? 'Replace plan image' : 'Upload plan image')}
          </Button>
          {plan.image_url && <Button variant="ghost" size="sm" onClick={() => setPlan(p => ({ ...p, image_url: '' }))} className="text-white/50" data-testid="floor-clear-image-btn">Remove image</Button>}
          <Button variant="ghost" size="sm" onClick={resetPlan} className="border border-white/10 text-white/70" data-testid="floor-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
          <Button size="sm" onClick={savePlan} disabled={saving} className="bg-primary-main text-black hover:bg-primary-hover font-heading tracking-wider" data-testid="floor-save-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save layout'}</Button>
          {msg && <span className="text-xs text-secondary-main" data-testid="floor-save-msg">{msg}</span>}
          {!msg && previewVersion && <span className="text-xs text-secondary-main" data-testid="floor-preview-badge">Previewing “{previewVersion.name}” — unsaved</span>}
        </div>
        <p className="text-[11px] text-white/35 mb-3">Background = the 1080×1920 portrait artwork behind everything (applied as soon as the upload finishes). Plan image = optional drawing placed behind the tables. Coordinates are board pixels (1080 across × 1920 down).</p>
        <div className="flex flex-wrap items-center gap-3 mb-3 rounded-lg border border-secondary-main/25 bg-secondary-main/5 px-3 py-2" data-testid="floor-template-box">
          <div className="flex-1 min-w-[260px] text-[11px] text-white/60">
            <b className="text-white">Make your artwork readable:</b> download the template, draw on top of it (keep the yellow legend box empty), export it as <b>1080 × 1920 px</b> and upload it as the background. Other sizes are stretched to fill the screen and get cropped.
          </div>
          <a href={`${API}/floor/template.png`} download className="inline-flex items-center h-8 px-3 rounded-md border border-secondary-main/40 text-secondary-main text-xs font-heading tracking-wider hover:bg-secondary-main/10" data-testid="floor-template-download">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Download template (PNG)
          </a>
        </div>
        {plan.bg_url && (
          <div className="flex items-center gap-3 mb-3">
            <img src={plan.bg_url} alt="" className="max-h-24 rounded-md border border-white/10" data-testid="floor-bg-preview" />
            <button onClick={() => applyBackground('')} className="text-xs text-white/50 hover:text-red-400" data-testid="floor-remove-bg-btn">Remove background</button>
          </div>
        )}
        <div className="mb-4">
          <p className="font-heading text-[11px] uppercase tracking-wider text-white/50 mb-2">Table design</p>
          <div className="flex flex-wrap gap-2" data-testid="floor-table-style-picker">
            {Object.entries(TABLE_STYLES).map(([key, label]) => (
              <button key={key} onClick={() => setPlan(p => ({ ...p, table_style: key }))} data-testid={`floor-table-style-${key}`}
                className={`px-3 py-1.5 rounded-lg border text-xs font-heading tracking-wider uppercase transition-colors ${
                  (plan.table_style || 'oval') === key ? 'border-primary-main bg-primary-main/10 text-white' : 'border-white/[0.08] text-white/60 hover:border-white/20'}`}>
                {label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-white/30 mt-1">Preview updates above; press Save layout to apply to the billboard.</p>
        </div>
        <div className="overflow-auto" style={{ maxHeight: 360 }}>
          <table className="w-full text-sm text-white/80" data-testid="floor-tables-editor">
            <thead className="text-[10px] uppercase tracking-wider text-white/40 sticky top-0 bg-background-surface">
              <tr><th className="text-left p-1">#</th><th className="text-left p-1">Zone</th><th className="p-1">X</th><th className="p-1">Y</th><th className="p-1">W</th><th className="p-1">H</th><th className="p-1">Rot</th><th /></tr>
            </thead>
            <tbody>
              {plan.tables.map((t, i) => (
                <tr key={i} className="border-t border-white/[0.04]" data-testid={`floor-editor-row-${t.num}`}>
                  <td className="p-1"><Input type="number" value={t.num} onChange={e => updateTable(i, 'num', e.target.value)} className="w-16 h-8 bg-background-base border-white/10 text-white" /></td>
                  <td className="p-1"><Input value={t.zone} onChange={e => updateTable(i, 'zone', e.target.value)} className="w-32 h-8 bg-background-base border-white/10 text-white" /></td>
                  {['x', 'y', 'w', 'h', 'rot'].map(f => (
                    <td key={f} className="p-1"><Input type="number" value={t[f] ?? 0} onChange={e => updateTable(i, f, e.target.value)} className="w-20 h-8 bg-background-base border-white/10 text-white" /></td>
                  ))}
                  <td className="p-1"><button onClick={() => removeTable(i)} className="text-white/30 hover:text-red-400" data-testid={`floor-remove-${t.num}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Button variant="ghost" size="sm" onClick={addTable} className="border border-white/10 text-white/70" data-testid="floor-add-table-btn"><Plus className="w-4 h-4 mr-1.5" /> Add table</Button>
          <span className="text-[11px] text-white/40">Table numbers must match the table numbers you open in PokerLens — open tables light up in the event's color, closed tables go back to white.</span>
        </div>
      </div>

      <FloorHistoryPanel token={token} refreshKey={historyKey} previewId={previewVersion?.id} onPreview={previewVersionPlan} onRestored={restoredVersion} />
    </div>
  );
};
